/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { BaseClient, checkStatus } from "@emt/emt-http-base-client";
import {
    Enrollment,
    PaymentAndTaxStatus,
    Invitation,
    NewEnrollmentRequest,
    AcceptInvitationResponse,
    EligibilityData,
    RoleInformationForUser, 
    DefaultPaymentProfileAssignmentDetails
} from "../models/model";
// import { userLocale } from "@emt/emt-localization/lib/user-locale";
import { LocalizationInfo } from "@emt/emt-localization/lib/localization-info";
import { getBaseUri, getPpmBaseUri, getDefaultRequestInit } from "../utilities/client-helper";

// const baseUrl = `/${userLocale}/dashboard/incentives/enrollment/api`;
const baseUrl = `${getBaseUri()}/dashboard/incentives/enrollment/api`;
const ppmBaseUrl = `${getPpmBaseUri()}/dashboard/payee/profiles/api/v1.0`;

const enrollmentsUrl = `${baseUrl}/v3.0/enrollments/list`;
const invitationsUrl = `${baseUrl}/v3.0/invitations/list`;
const payoutStatusUrl = `${baseUrl}/v3.0/payoutprofile/list`;
const partnerProgramPayoutStatusUrl = `${baseUrl}/v3.0/payoutprofile/partner`;
const newEnrollmentUrl = `${baseUrl}/v3.0/enrollments/new`;
const eligibilityUrl = `${baseUrl}/v3.0/eligibility/status`;
const rolesUrl = `${baseUrl}/v3.0/userpermissions/roles`;
const defaultPaymentUrl = `${ppmBaseUrl}/partner-preferences/default-payment-profile/assignment`;

export class EnrollmentClient extends BaseClient {
    async getLocStrings(): Promise<LocalizationInfo|null> {
        try {
            const url = `${baseUrl}/localization`;
            const response = await this.fetch<any>(url, undefined, await getDefaultRequestInit(false, true));
            //console.log("ResponseLoc: ", response);
            const data = JSON.parse(JSON.stringify(response));
            
            // Create a proper LocalizationInfo object
            // The constructor expects: (strings, ltr, learnMoreIds, lcid)
            const strings = data.locStrings || {};
            const ltr = data.ltr !== undefined ? data.ltr : true;
            const learnMoreIds = new Map<string, number>([])
            const lcid = data.lcid || 1033;
            
            const locInfo = new LocalizationInfo(strings, ltr, learnMoreIds, lcid);
            console.log("Localization data (converted):", locInfo);
            return locInfo;
        }
        catch (error) {
            console.log("Error fetching localization data: ", error);
            return null;
        }
    }

    async getEnrollments(): Promise<Enrollment[]> {
        //const enrUrl = `${getBaseUri()}/dashboard/incentives/enrollment/api/v3.0/enrollments/list`;
        return this.fetch<Enrollment[]>(enrollmentsUrl, undefined, await getDefaultRequestInit());
    }

    async getInvitations(): Promise<Invitation[]> {
        return this.fetch<Invitation[]>(invitationsUrl, undefined, await getDefaultRequestInit());
    }

    async startNewEnrollment(newEnrollmentRequests: NewEnrollmentRequest[]): Promise<AcceptInvitationResponse[]> {
        return this.post<NewEnrollmentRequest[], AcceptInvitationResponse[]>(newEnrollmentUrl, newEnrollmentRequests, await getDefaultRequestInit(true));
    }

    async getBankAndTaxStatus(): Promise<PaymentAndTaxStatus[]> {
        return this.fetch<PaymentAndTaxStatus[]>(payoutStatusUrl, undefined, await getDefaultRequestInit());
    }

    async getBankAndTaxStatusForPartnerAndProgram(mpn: string, programGuid: string): Promise<PaymentAndTaxStatus> {
        const url = `${partnerProgramPayoutStatusUrl}?mpnNumber=${mpn}&programGuid=${programGuid}`;
        return this.fetch<PaymentAndTaxStatus>(url, undefined, await getDefaultRequestInit());
    }

    async getEligibilityForProgramAndMpn(mpn: string, programGuid: string): Promise<EligibilityData> {
        var url = eligibilityUrl + `?partnerIdType=MPN&partnerIdValue=${mpn}&programGuid=${programGuid}`;
        return this.fetch<EligibilityData>(url, undefined, await getDefaultRequestInit());
    }

    async getRolesPermissionsForUserAwareness(): Promise<RoleInformationForUser> {
        return this.fetch<RoleInformationForUser>(rolesUrl, undefined, await getDefaultRequestInit());
    }

    async getDefaultPaymentProfiles(partnerValue: string, programGuid:string): Promise<DefaultPaymentProfileAssignmentDetails> { 
        var url = `${defaultPaymentUrl}/${partnerValue}/${programGuid}`;
        return this.fetch<DefaultPaymentProfileAssignmentDetails>(url, undefined, await getDefaultRequestInit());
    }

    async applyDefaultPaymentProfileSettings(partnerValue: string, programGuid:string) {

        var url = `${defaultPaymentUrl}/${partnerValue}/${programGuid}`;
        const fetchOptions:RequestInit = {
            credentials: "same-origin",
            headers: { 'Content-Type': 'application/json' },
            method: "POST"
        };
        const response = await fetch(url, fetchOptions);
        return checkStatus(response);
    }
}
